import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, MapPin, Phone, Mail, Award, Users, Scale, Heart,
  Calendar, GraduationCap, Download, Send, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { SiFacebook, SiWhatsapp } from 'react-icons/si';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Layout } from '@/components/Layout';
import { NewsCard, CoreValueCard, StatCard, JoinStepCard } from '@/components/Cards';
import { coreValues, joinSteps, statsData } from '@/data/index';
import { ContactFormData, Announcement, ROUTE_PATHS } from '@/lib/index';
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { IMAGES } from '@/assets/images';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const contactFormSchema = z.object({
  name: z.string().min(2, '姓名至少需要2個字符'),
  email: z.string().email('請輸入有效的電郵地址'),
  phone: z.string().min(8, '請輸入有效的電話號碼'),
  message: z.string().min(10, '查詢內容至少需要10個字符')
});

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Award,
  Users,
  Scale,
  Heart,
  Calendar,
  GraduationCap,
  Shield
};

export default function Home() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [annoImgIndex, setAnnoImgIndex] = useState(0);
  const navigate = useNavigate();

  // Auto-advance announcement images every 5 seconds
  useEffect(() => {
    if (!announcement || announcement.images.length <= 1) return;
    const timer = setInterval(() => {
      setAnnoImgIndex(i => (i + 1) % announcement.images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [announcement]);

  useEffect(() => {
    async function fetchAnnouncement() {
      const { data } = await supabase
        .from('announcements_2026_04_15')
        .select('*')
        .eq('published', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();
      if (data) {
        const raw = data as {
          id: string;
          title: string;
          content: string;
          images: unknown;
          published: boolean;
          created_at: string;
          updated_at: string;
        };
        setAnnouncement({
          ...raw,
          images: Array.isArray(raw.images) ? raw.images : []
        } as Announcement);
      } else {
        setAnnouncement(null);
      }
    }
    fetchAnnouncement();

    // Realtime — instantly reflect admin changes
    const channel = supabase
      .channel('home-announcements-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'announcements_2026_04_15' },
        () => { fetchAnnouncement(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema)
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('send-inquiry-email-2026', {
        body: data
      });
      if (error) throw error;
      toast.success('查詢已成功提交！我們會在3個工作天內回覆您，並已發送確認電郵至您的信箱。');
      reset();
    } catch (_err) {
      toast.error('提交失敗，請稍後再試，或直接致電 +852 5228 4182。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* ── HERO ── */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-emerald-50" />
        <img
          src={IMAGES.TCM_HERO_7}
          alt="Traditional Chinese Medicine"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />

        <motion.div
          className="relative z-10 container mx-auto px-4 text-center"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div variants={staggerItem}>
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 px-4 py-2 text-base">
              <Shield className="w-4 h-4 mr-2" />
              守護中醫師權益
            </Badge>
          </motion.div>

          <motion.h1
            className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight"
            variants={staggerItem}
          >
            香港中醫師權益總工會
          </motion.h1>

          <motion.p className="text-sm sm:text-lg md:text-2xl text-muted-foreground mb-3 px-2" variants={staggerItem}>
            Hong Kong Chinese Medicine Practitioners' Rights General Union
          </motion.p>

          <motion.p
            className="text-sm sm:text-base md:text-xl text-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed px-2"
            variants={staggerItem}
          >
            成立於2003年，致力保障香港中醫師的專業權益，推動中醫藥行業健康發展，為會員提供全面專業支援。
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row gap-3 justify-center mb-10 px-4" variants={staggerItem}>
            <Button
              size="lg"
              className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
              onClick={() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              立即加入
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
              onClick={() => {
                const el = document.getElementById('about');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              了解更多
            </Button>
          </motion.div>

          <motion.div
            className="grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto px-2"
            variants={staggerContainer}
          >
            {statsData.map((stat) => {
              const _Icon = iconMap[stat.icon];
              return (
                <motion.div key={stat.label} variants={staggerItem}>
                  <StatCard number={stat.value} label={stat.label} />
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 text-foreground">
                關於我們
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                香港中醫師權益總工會成立於2003年，是香港首個專為中醫師權益設立的專業工會組織。我們致力於保障香港中醫師的專業權益，促進中醫藥行業的健康發展，為會員提供全面的專業支援，推動中醫藥在香港醫療體系中的重要地位。
              </p>

              <Card className="mb-6 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-2xl text-primary">我們的使命</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    保障香港中醫師的專業權益，促進中醫藥行業的健康發展，為會員提供全面的專業支援，推動中醫藥在香港醫療體系中的重要地位。
                  </p>
                </CardContent>
              </Card>

              <Card className="border-accent/20">
                <CardHeader>
                  <CardTitle className="text-2xl text-accent">我們的願景</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    成為香港最具影響力的中醫師專業組織，推動中醫藥現代化發展，讓中醫師在香港醫療體系中獲得應有的尊重和地位。
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={staggerItem}>
              <img
                src="/images/hkcmprgu-logo-02.png"
                alt="香港中醫師權益總工會"
                className="rounded-2xl shadow-lg w-full h-auto object-contain bg-white p-6"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CORE VALUES ── */}
      <section id="values" className="py-16 md:py-24 bg-gradient-to-br from-primary via-primary to-accent">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-10 md:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-primary-foreground">
              核心價值
            </h2>
            <p className="text-sm sm:text-base md:text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              我們堅守的專業理念與服務承諾
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            {coreValues.map((value) => (
              <motion.div key={value.id} variants={staggerItem}>
                <CoreValueCard value={value} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── NEWS (Facebook Feed) ── */}
      <section id="news" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-10 md:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-foreground">
              最新動向
            </h2>
            <p className="text-sm sm:text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
              關注我們的 Facebook 專頁，掌握工會最新動態
            </p>
          </motion.div>

          {/* Facebook + Announcement side by side */}
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            {/* Facebook iframe — left half */}
            <motion.div variants={staggerItem} className="w-full">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-border bg-white w-full" style={{ height: 700, display: 'flex', flexDirection: 'column' }}>
                <iframe
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fp%2F%E9%A6%99%E6%B8%AF%E4%B8%AD%E9%86%AB%E5%B8%AB%E6%AC%8A%E7%9B%8A%E7%B8%BD%E5%B7%A5%E6%9C%83-61571583053507%2F&tabs=timeline&width=500&height=700&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false&appId"
                  width="100%"
                  height="700"
                  style={{ border: 'none', overflow: 'hidden', display: 'block', width: '100%', flex: 1 }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="香港中醫師權益總工會 Facebook專頁"
                />
              </div>
              <div className="mt-3 flex justify-center">
                <Button variant="outline" size="sm" asChild className="gap-2">
                  <a
                    href="https://www.facebook.com/p/%E9%A6%99%E6%B8%AF%E4%B8%AD%E9%86%AB%E5%B8%AB%E6%AC%8A%E7%9B%8A%E7%B8%BD%E5%B7%A5%E6%9C%83-61571583053507/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SiFacebook className="w-4 h-4 text-[#1877F2]" />
                    前往 Facebook 專頁
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </Button>
              </div>
            </motion.div>

            {/* Announcement card — right half, match Facebook height */}
            <motion.div variants={staggerItem} className="w-full">
              {announcement ? (
                /* Clickable card → announcement detail page */
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(ROUTE_PATHS.ANNOUNCEMENT)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(ROUTE_PATHS.ANNOUNCEMENT)}
                  className="cursor-pointer group h-full"
                  style={{ minHeight: 700 }}
                  aria-label="查看完整公告"
                >
                  <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 transition-shadow group-hover:shadow-lg group-hover:border-primary/40 overflow-auto" style={{ height: 700 }}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <Badge className="w-fit bg-primary/10 text-primary border-primary/20">最新公告</Badge>
                        <span className="text-xs text-primary font-medium">點擊查看詳情 →</span>
                      </div>
                      <CardTitle className="text-primary text-lg leading-snug mt-2">{announcement.title}</CardTitle>
                      <CardDescription className="text-xs">
                        {new Date(announcement.updated_at).toLocaleDateString('zh-HK', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {announcement.images.length > 0 && (
                        <div className="relative rounded-lg overflow-hidden">
                          <img
                            src={announcement.images[annoImgIndex]?.url}
                            alt={`公告圖片 ${annoImgIndex + 1}`}
                            className="w-full h-44 object-cover rounded-lg transition-transform duration-500"
                          />
                          {announcement.images.length > 1 && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setAnnoImgIndex(i => (i - 1 + announcement.images.length) % announcement.images.length); }}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors"
                                aria-label="上一張"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setAnnoImgIndex(i => (i + 1) % announcement.images.length); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors"
                                aria-label="下一張"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                {announcement.images.map((_, i) => (
                                  <button
                                    key={i}
                                    onClick={(e) => { e.stopPropagation(); setAnnoImgIndex(i); }}
                                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                      i === annoImgIndex ? 'bg-white' : 'bg-white/50'
                                    }`}
                                    aria-label={`圖片 ${i + 1}`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      <p
                        className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: announcement.images.length > 0 ? 6 : 14,
                          WebkitBoxOrient: 'vertical' as const,
                          overflow: 'hidden',
                          wordBreak: 'break-word'
                        }}
                      >
                        {announcement.content}
                      </p>
                      {/* Show ellipsis hint if content is likely truncated */}
                      {announcement.content.length > (announcement.images.length > 0 ? 180 : 420) && (
                        <p className="text-xs text-primary/70 mt-1">......</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="border-border h-full">
                  <CardHeader>
                    <CardTitle className="text-muted-foreground text-base">工會動向公告</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      暫時沒有最新公告，請關注我們的 Facebook 專頁獲取最新資訊。
                    </p>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-10 md:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-foreground">
              聯絡我們
            </h2>
            <p className="text-sm sm:text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
              歡迎查詢或申請加入工會
            </p>
          </motion.div>

          <motion.div
            className="grid lg:grid-cols-2 gap-6 md:gap-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            {/* Left: contact info */}
            <motion.div variants={staggerItem} className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-primary" />
                    地址
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    油麻地窩打老道40號4至5號舖<br />
                    （寶翠大廈40號地下，油麻地地鐵站D出口左轉）
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-primary" />
                    電話 / WhatsApp
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-muted-foreground font-mono">+852 5228 4182</p>
                  <a
                    href="https://wa.me/85252284182"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <SiWhatsapp className="w-4 h-4 text-green-500" />
                    點擊 WhatsApp 聯絡我們
                  </a>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    電郵
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href="mailto:inquiry@hkcmprgu.org.hk"
                    className="text-muted-foreground font-mono hover:text-primary transition-colors"
                  >
                    inquiry@hkcmprgu.org.hk
                  </a>
                </CardContent>
              </Card>

              {/* Join steps */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-primary">加入工會</CardTitle>
                  <CardDescription>跟隨以下步驟完成入會申請</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {joinSteps.map((step) => (
                      <JoinStepCard key={step.id} step={step} />
                    ))}
                  </div>

                  {/* Form Download */}
                  <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
                    <p className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      下載申請表
                    </p>
                    <a
                      href="https://mhwyqgpnsqdydorjakkq.supabase.co/storage/v1/object/public/member%20application%20form/HKCMPRGU_MAF.pdf"
                      download="正式會員申請表_HKCMPRGU.pdf"
                      className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      正式會員申請表 (HKCMPRGU)
                    </a>
                    <p className="text-xs text-muted-foreground mt-3">
                      下載後可直接列印。如有疑問歡迎 WhatsApp 或電郵聯絡本會。
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right: inquiry form */}
            <motion.div variants={staggerItem}>
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5 text-primary" />
                    查詢表格
                  </CardTitle>
                  <CardDescription>
                    填寫以下資料，提交後我們會盡快回覆，並自動發送確認電郵。
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <Input
                        placeholder="姓名 *"
                        {...register('name')}
                        className="bg-muted"
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <Input
                        type="email"
                        placeholder="電郵地址 *"
                        {...register('email')}
                        className="bg-muted"
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <Input
                        placeholder="聯絡電話 *"
                        {...register('phone')}
                        className="bg-muted"
                      />
                      {errors.phone && (
                        <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
                      )}
                    </div>

                    <div>
                      <Textarea
                        placeholder="查詢內容 *"
                        rows={6}
                        {...register('message')}
                        className="bg-muted"
                      />
                      {errors.message && (
                        <p className="text-sm text-destructive mt-1">{errors.message.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full gap-2"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                          提交中...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          提交查詢
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      提交後，查詢通知將發送至本會，同時您會收到確認電郵。
                    </p>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
